import { useNcmMutation } from '@/core/domain/operational/Ncm/Ncm.hooks';
import { Ncm } from '@/core/domain/operational/Ncm/Ncm.types';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import { useCallback, useMemo } from 'react';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import {
  IcmsBaseCalculationDeterminationMode,
  InvoiceIcmsCstCsosn,
  IpiCst,
  PisCofinsCst,
  TaxRegimeEnum,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import {
  optionsCsosnCodes,
  optionsCstCodes,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.const';
import { usePartMutation } from '@/core/domain/operational/Parts/Parts.hooks';
import { usePartStockPricesMutation } from '@/core/domain/stock/StockItems/StockItems.hooks';
import { Part } from '@/core/domain/operational/Parts/Parts.types';
import { useInvoiceNoteItemDetail } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItms.hooks';
import {
  InvoicePartFormValues,
  InvoicePartNcmChangeFormValues,
} from './InvoicePartForm.types';
import { applyMask } from '@/utils/mask';
import { cestMask } from '@/utils/mask/commonMasks';
import { Insurer } from '@/core/domain/people/Insurers/Insurers.types';
import { useInvoiceFormContext } from '../../InvoiceFormProvider';
import { useInsurerMutation } from '@/core/domain/people/Insurers/Insurers.hooks';
import { useCustomerGeneralMutation } from '@/core/domain/people/Customers/Customers.hooks';
import { CustomerGeneral } from '@/core/domain/people/Customers/Customers.types';
import { taxesMeiOptions } from '@/components/_common/TaxesSelect/TaxeIcmsCsosnSelect';
import moment from 'moment';
import {
  InvoiceSettingConfigNFc,
  InvoiceSettingConfigNFe,
} from '@/core/domain/invoice/settings/Settings.types';

interface Props {
  companyId?: number;
  ncmIdSelected?: number;
}

interface Params {
  part?: Part;
  ncmId?: number;
  invoiceType?: InvoiceType;
  formValues: Partial<InvoicePartFormValues>;
}

type HookReturn = (params: Params) => Promise<Partial<InvoicePartFormValues>>;

const calculationDeterminationMode: Record<
  number,
  IcmsBaseCalculationDeterminationMode
> = {
  0: IcmsBaseCalculationDeterminationMode.MARGIN_VALUE_ADDED,
  1: IcmsBaseCalculationDeterminationMode.TABLE_PRICE_MAX,
  2: IcmsBaseCalculationDeterminationMode.LIST_PRICE_MAX,
  3: IcmsBaseCalculationDeterminationMode.OPERATION_VALUE,
};

export function useInvoicePartFillFields(props: Props): HookReturn {
  const ncmDetail = useNcmMutation();
  const customerDetail = useCustomerGeneralMutation();
  const insurierDetail = useInsurerMutation();
  const invoiceSettingsData = useInvoiceSettingsByCompany(props.companyId);
  const {
    invoice: { data },
  } = useInvoiceFormContext();

  const fillInFieldsIrpjCsll = async (aliquot?: number) => {
    try {
      let customer: CustomerGeneral | undefined, insurier: Insurer | undefined;

      if (data?.customerId) {
        customer = await customerDetail.mutateAsync(data?.customerId);
      }

      if (data?.insurerId) {
        insurier = await insurierDetail.mutateAsync(data?.insurerId);
      }

      const irpjCsllSelected =
        customer?.retentionIRPJorCSLL || insurier?.retentionIRPJorCSLL;

      return irpjCsllSelected ? aliquot : undefined;
    } catch (error) {}
  };

  return useCallback(
    async (params: Params): Promise<InvoicePartFormValues> => {
      const formValues = params.formValues;
      const part = params?.part;

      let ncm = {} as Ncm | undefined;
      let invoiceSettings:
        | InvoiceSettingConfigNFc
        | InvoiceSettingConfigNFe
        | undefined;

      if (params.invoiceType === InvoiceType.NFC) {
        invoiceSettings = invoiceSettingsData.data?.configNfc;
      } else if (params.invoiceType === InvoiceType.NFE) {
        invoiceSettings = invoiceSettingsData.data?.configNfe;
      }

      if (params.ncmId) {
        ncm = await ncmDetail.mutateAsync(props.ncmIdSelected || params.ncmId);
      }

      const getIcmsType = (taxRegime?: TaxRegimeEnum, type?: string) => {
        if (
          taxRegime === TaxRegimeEnum.simple ||
          taxRegime === TaxRegimeEnum.simplesNationalMei
        ) {
          return optionsCsosnCodes
            .filter((option) =>
              taxRegime === TaxRegimeEnum.simplesNationalMei
                ? taxesMeiOptions.includes(option.code)
                : true,
            )
            .find((a) => a.code === type)?.id;
        } else {
          return optionsCstCodes.find((a) => a.code === type)?.id;
        }
      };

      const isTaxRegimeSimpleOrMEI =
        invoiceSettings?.taxRegimeDefault === TaxRegimeEnum.simple ||
        invoiceSettings?.taxRegimeDefault === TaxRegimeEnum.simplesNationalMei;

      const icsmCstCsosn =
        getIcmsType(
          invoiceSettings?.taxRegimeDefault,
          isTaxRegimeSimpleOrMEI ? ncm?.csosnIcms : ncm?.cstIcms,
        ) || invoiceSettings?.icmsCstDefault;
      


      const csllWithheldRate = await fillInFieldsIrpjCsll(
        invoiceSettings?.csllAliquotDefault,
      );
      const irrfWithheldRate = await fillInFieldsIrpjCsll(
        invoiceSettings?.irrfAliquotDefault,
      );

      const approximateTaxValue =
        ((formValues.general?.valueUnitary ?? 0) *
          (formValues.general?.quantity ?? 1) *
          (ncm?.taxesPercentage ?? 0)) /
        100;

      const pisCst = (ncm?.cstPis ||
        invoiceSettings?.pisCstDefault) as PisCofinsCst;
      const cofinsCst = (ncm?.cstCofins ||
        invoiceSettings?.cofinsCstDefault) as PisCofinsCst;

      const getValidAliquot = (firstValue?: number, secondValue?: number) =>
        Number.isFinite(firstValue)
          ? firstValue
          : Number.isFinite(secondValue)
          ? secondValue
          : undefined;

      const pisAliquot = getValidAliquot(
        ncm?.aliquotPis,
        invoiceSettings?.pisAliquotDefault,
      );

      const cofinsAliquot = getValidAliquot(
        ncm?.aliquotCofins,
        invoiceSettings?.cofinsAliquotDefault,
      );

      const ipiAliquot = getValidAliquot(
        ncm?.aliquotIpi,
        invoiceSettings?.ipiAliquotDefault,
      );

      const icmsCst = icsmCstCsosn;

      const icmsSimpleNationalCreditPercentage =
        (invoiceSettings?.taxRegimeDefault === TaxRegimeEnum.simple &&
          (icmsCst ===
            InvoiceIcmsCstCsosn.TAXATION_UNDER_SIMPLES_WITH_CREDIT_PERMISSION ||
            icmsCst ===
              InvoiceIcmsCstCsosn.SIMPLES_NACIONAL_WITH_CREDIT_PERMISSION_AND_ICMS_BY_TAX_SUBSTITUTION) &&
          invoiceSettings.simpleNationalCreditRateDefault) ||
        formValues?.itemTaxes?.icms.icmsSimpleNationalCreditPercentage;

      return {
        general: {
          ...formValues?.general,
          cest: ncm?.cest ? applyMask(ncm?.cest, cestMask) : undefined,
          approximateTaxValue,
        },

        fuel: {
          ...formValues?.fuel,
          anpCode: part?.anpCode,
          anpDescription: part?.anpDescription,
        },

        itemTaxes: {
          icms: {
            ...formValues?.itemTaxes?.icms,
            icmsStValueAddedMarginPercentage: ncm?.mva,
            icmsBaseCalculationDeterminationMode:
              calculationDeterminationMode[
                ncm?.modalityDeterminationBcIcms as number
              ] || invoiceSettings?.icmsBaseCalculationDeterminationModeDefault,
            taxRegime: invoiceSettings?.taxRegimeDefault,
            icmsCst: icmsCst,
            icmsOrigin: part?.icmsOrigin || invoiceSettings?.icmsOriginDefault,
            icmsAliquot:
              ncm?.aliquotIcms ?? invoiceSettings?.icmsAliquotDefault,
            icmsBaseCalculationReductionPercentage: ncm?.reductionIcms,
            shouldNotDeductExemptIcms:
              invoiceSettings?.shouldNotDeductExemptIcmsDefault,
            icmsSimpleNationalCreditPercentage,
          },

          share: {
            ...formValues?.itemTaxes?.share,
          },

          ipi: {
            ...formValues?.itemTaxes?.ipi,
            ipiAliquot: ipiAliquot,
            ipiCst: (ncm?.cstIpi || invoiceSettings?.ipiCstDefault) as IpiCst,
            ipiLegalClassificationCode:
              invoiceSettings?.ipiLegalClassificationCodeDefault,
          },

          pis: {
            ...formValues?.itemTaxes?.pis,
            pisAliquot:
              pisCst === PisCofinsCst.TAXABLE_OPERATION_MONOPHASIC
                ? undefined
                : pisAliquot,
            pisCst,
          },

          cofins: {
            ...formValues?.itemTaxes?.cofins,
            cofinsAliquot:
              cofinsCst === PisCofinsCst.TAXABLE_OPERATION_MONOPHASIC
                ? undefined
                : cofinsAliquot,
            cofinsCst,
          },

          irpjCsll: {
            csllWithheldRate,
            irrfWithheldRate,
          },

          is: {
            ...formValues?.itemTaxes?.is,
            selectiveTaxAliquot:
              ncm?.selectiveTaxAliquot ??
              invoiceSettings?.selectiveTaxAliquotDefault,
            selectiveTaxCst:
              ncm?.selectiveTaxCst || invoiceSettings?.selectiveTaxCstDefault,
            selectiveTaxClassificationCode:
              ncm?.selectiveTaxClassificationCode ||
              invoiceSettings?.selectiveTaxClassificationCodeDefault,
          },

          ibsCbs: {
            ...formValues?.itemTaxes?.ibsCbs,
            ibsCbsCst: ncm?.ibsCbsCst || invoiceSettings?.ibsCbsCstDefault,
            ibsCbsTaxClassification:
              ncm?.ibsCbsTaxClassification ||
              invoiceSettings?.ibsCbsTaxClassificationDefault,
            ibsUfAliquot:
              ncm?.ibsUfAliquotDefault ?? invoiceSettings?.ibsUfAliquotDefault,
            ibsMunicipalAliquot:
              ncm?.ibsMunicipalAliquotDefault ??
              invoiceSettings?.ibsMunicipalAliquotDefault,
            cbsAliquot: ncm?.cbsAliquot ?? invoiceSettings?.cbsAliquot,
          },
        },
      };
    },
    [invoiceSettingsData.data, props.companyId],
  );
}

interface CreateFillFieldsProps {
  companyId?: number;
  invoiceType?: InvoiceType;
}

interface CreateFillFieldsParams {
  invoiceId?: number;
  invoiceType: InvoiceType;
  partId?: number;
  ncmIdSelected?: number;
}

type CreteFillFieldsReturm = [
  (params: CreateFillFieldsParams) => Promise<Partial<InvoicePartFormValues>>,
  boolean,
];

export function useInvoicePartCreateFillFields(
  props: CreateFillFieldsProps,
): CreteFillFieldsReturm {
  const { invoiceSettingNfe, invoiceSettingNfc, invoiceSettingNfs } =
    useInvoiceFormContext();

  const invoiceSetting =
    props.invoiceType === InvoiceType.NFE
      ? invoiceSettingNfe
      : props.invoiceType === InvoiceType.NFC
      ? invoiceSettingNfc
      : invoiceSettingNfs;

  const partDetail = usePartMutation();
  const partStockPrices = usePartStockPricesMutation();

  const isLoading = useMemo(
    () => partDetail.isLoading || partStockPrices.isLoading,
    [partDetail.isLoading, partStockPrices.isLoading],
  );

  const invoicePartFillFields = useInvoicePartFillFields({
    companyId: props.companyId,
  });

  const fetchFields = useCallback(
    async (params: CreateFillFieldsParams) => {
      let part: Part | undefined;
      let valueUnitary: number | undefined;

      if (params.partId) {
        part = await partDetail.mutateAsync(params?.partId);

        if (props.companyId) {
          const stockPrices = await partStockPrices.mutateAsync(params.partId);

          valueUnitary = stockPrices?.result?.find(
            (price) => price.company.id === props.companyId,
          )?.sellPrice;
        }
      }

      const fields = await invoicePartFillFields({
        invoiceType: params.invoiceType,
        ncmId: params.ncmIdSelected || part?.ncmId,
        part,
        formValues: {
          general: {
            description: part?.name,
            barCode: part?.barCode,
            partId: params?.partId,
            ncmId: params.ncmIdSelected || part?.ncmId,
            cfopId: part?.cfopId,
            unitMeasurement:
              part?.unit || invoiceSetting?.unitMeasurementDefault,
            quantity: 1,
            valueUnitary: valueUnitary || 0,
            valueTotal: 1 * (valueUnitary || 0) - 0,
          },
        },
      });

      return fields;
    },
    [props],
  );

  return [fetchFields, isLoading];
}

type DetailFillFieldsReturn = [
  (
    invoiceItemId?: number,
    invoiceType?: InvoiceType,
  ) => Promise<InvoicePartFormValues>,
  boolean,
];

export function useInvoicePartDetailFillFieds(): DetailFillFieldsReturn {
  const invoiceNoteItemDetail = useInvoiceNoteItemDetail();

  const fetchFields = useCallback(
    async (invoiceItemId?: number): Promise<InvoicePartFormValues> => {
      const data = await invoiceNoteItemDetail.mutateAsync(invoiceItemId);

      const cest = data?.cest ? applyMask(data?.cest, cestMask) : undefined;

      const icmsCst = data?.itemTaxes?.icmsCst;

      const icmsAliquot = data?.itemTaxes?.icmsAliquot;

      const icmsBaseCalculationDeterminationMode =
        data?.itemTaxes?.icmsBaseCalculationDeterminationMode;

      const valueTotal =
        (data?.quantity || 0) * (data?.valueUnitary || 0) +
        (data?.freightValue || 0) +
        (data?.insuranceValue || 0) +
        (data?.otherValue || 0) -
        (data?.valueDiscount || 0);

      return {
        general: {
          cest,
          nve: data?.nve,
          ncmId: data?.ncmId,
          partId: data?.partId,
          consumableId: data?.consumableId,
          description: data?.description,
          unitMeasurement: data?.unitMeasurement,
          cfopId: data?.cfopId,
          quantity: data?.quantity,
          valueUnitary: data?.valueUnitary,
          barCode: data?.barCode,
          otherValue: data?.otherValue,
          valueDiscount: data?.valueDiscount,
          freightValue: data?.freightValue,
          insuranceValue: data?.insuranceValue,
          valueTotal: valueTotal,
          relevanceScaleIndicator: data?.relevanceScaleIndicator,
          manufacturerMerchandiseCnpj: data?.manufacturerMerchandiseCnpj,
          taxBenefitCode: data?.taxBenefitCode,
          approximateTaxValue: data?.approximateTaxValue,
          purchaseNumber: data?.purchaseNumber,
          purchaseOrder: data?.purchaseOrder,
          exTipi: data?.exTipi,
          additionalInformation: data?.additionalInformation,
          keyNfePurchase: data?.keyNfePurchase,
          itemNumberPurchase: data?.itemNumberPurchase,
        },
        itemTaxes: {
          icms: {
            icmsCst,
            icmsAliquot,
            icmsBaseCalculationReductionPercentage:
              data?.itemTaxes?.icmsBaseCalculationReductionPercentage,
            icmsBaseCalculationDeterminationMode,
            taxRegime: data?.itemTaxes?.taxRegime,
            icmsOrigin: data?.itemTaxes?.icmsOrigin,
            icmsSimpleNationalCreditPercentage:
              data?.itemTaxes?.icmsSimpleNationalCreditPercentage,
            icmsSimpleNationalCreditValue:
              data?.itemTaxes?.icmsSimpleNationalCreditValue,
            icmsBaseCalculationValue: data?.itemTaxes?.icmsBaseCalculationValue,
            icmsValue: data?.itemTaxes?.icmsValue,
            icmsStAliquot: data?.itemTaxes?.icmsStAliquot,
            icmsStDeterminationMode: data?.itemTaxes?.icmsStDeterminationMode,
            icmsStBaseCalculationValue:
              data?.itemTaxes?.icmsStBaseCalculationValue,
            icmsStBaseCalculationReductionPercentage:
              data?.itemTaxes?.icmsStBaseCalculationReductionPercentage,
            icmsStValueAddedMarginPercentage:
              data?.itemTaxes?.icmsStValueAddedMarginPercentage,
            icmsDestinyUfBaseCalculationValue:
              data?.itemTaxes?.icmsDestinyUfBaseCalculationValue,
            icmsDestinyUfValue: data?.itemTaxes?.icmsDestinyUfValue,
            icmsStValue: data?.itemTaxes?.icmsStValue,
            icmsStFcpAliquot: data?.itemTaxes?.icmsStFcpAliquot,
            icmsStFcpBaseCalculationValue:
              data?.itemTaxes?.icmsStFcpBaseCalculationValue,
            icmsStFcpValue: data?.itemTaxes?.icmsStFcpValue,
            icmsFcpAliquot: data?.itemTaxes?.icmsFcpAliquot,
            icmsFcpBaseCalculationValue:
              data?.itemTaxes?.icmsFcpBaseCalculationValue,
            icmsExemptionValue: data?.itemTaxes?.icmsExemptionValue,
            icmsExemptionReason: data?.itemTaxes?.icmsExemptionReason,
            icmsDefermentPercentage: data?.itemTaxes?.icmsDefermentPercentage,
            icmsDefermentValue: data?.itemTaxes?.icmsDefermentValue,
            icmsDefermentDueValue: data?.itemTaxes?.icmsDefermentDueValue,
            icmsEffectiveBaseCalculationValue:
              data?.itemTaxes?.icmsEffectiveBaseCalculationValue,
            icmsEffectiveBaseCalculationReductionPercentage:
              data?.itemTaxes?.icmsEffectiveBaseCalculationReductionPercentage,
            icmsEffectiveAliquot: data?.itemTaxes?.icmsEffectiveAliquot,
            icmsEffectiveValue: data?.itemTaxes?.icmsEffectiveValue,
            shouldNotDeductExemptIcms:
              data.itemTaxes?.shouldNotDeductExemptIcms,
          },

          share: {
            icmsBaseCalculationShare: data?.itemTaxes?.icmsBaseCalculationShare,
            fcpBaseCalculationShare: data?.itemTaxes?.fcpBaseCalculationShare,
            icmsFcpPercentageShare: data?.itemTaxes?.icmsFcpPercentageShare,
            internalAliquotShare: data?.itemTaxes?.internalAliquotShare,
            interstateAliquotShare: data?.itemTaxes?.interstateAliquotShare,
            fcpRelativeIcmsShare: data?.itemTaxes?.fcpRelativeIcmsShare,
            interstateIcmsShare: data?.itemTaxes?.interstateIcmsShare,
          },

          ipi: {
            ipiCst: data?.itemTaxes?.ipiCst,
            ipiAliquot: data?.itemTaxes?.ipiAliquot,
            ipiLegalClassificationCode:
              data?.itemTaxes?.ipiLegalClassificationCode,
            ipiProducerCnpj: data?.itemTaxes?.ipiProducerCnpj,
            ipiBaseCalculation: data?.itemTaxes?.ipiBaseCalculation,
            ipiValue: data?.itemTaxes?.ipiValue,
            ipiControlSealCode: data?.itemTaxes?.ipiControlSealCode,
            ipiControlSealQuantity: data?.itemTaxes?.ipiControlSealQuantity,
            ipiUnitValue: data?.itemTaxes?.ipiUnitValue,
            ipiUnitQuantity: data?.itemTaxes?.ipiUnitQuantity,
            returnedGoodsPercentage: data?.itemTaxes?.returnedGoodsPercentage,
            returnedIpiValue: data?.itemTaxes?.returnedIpiValue,
          },

          pis: {
            pisCst: data?.itemTaxes?.pisCst,
            pisAliquot: data?.itemTaxes?.pisAliquot,
            pisValue: data?.itemTaxes?.pisValue,
            pisBaseCalculationValue: data?.itemTaxes?.pisBaseCalculationValue,
            pisSoldQuantity: data?.itemTaxes?.pisSoldQuantity,
            pisRealAliquot: data?.itemTaxes?.pisRealAliquot,
            pisStAliquot: data?.itemTaxes?.pisStAliquot,
            pisStValue: data?.itemTaxes?.pisStValue,
            pisStBaseCalculation: data?.itemTaxes?.pisStBaseCalculation,
            pisStSoldQuantity: data?.itemTaxes?.pisStSoldQuantity,
            pisStRealAliquot: data?.itemTaxes?.pisStRealAliquot,
          },

          cofins: {
            cofinsCst: data?.itemTaxes?.cofinsCst,
            cofinsAliquot: data?.itemTaxes?.cofinsAliquot,
            cofinsValue: data?.itemTaxes?.cofinsValue,
            cofinsBaseCalculationValue:
              data?.itemTaxes?.cofinsBaseCalculationValue,
            cofinsSoldQuantity: data?.itemTaxes?.cofinsSoldQuantity,
            cofinsRealAliquot: data?.itemTaxes?.cofinsRealAliquot,
            cofinsStAliquot: data?.itemTaxes?.cofinsStAliquot,
            cofinsStValue: data?.itemTaxes?.cofinsStValue,
            cofinsStBaseCalculation: data?.itemTaxes?.cofinsStBaseCalculation,
            cofinsStSoldQuantity: data?.itemTaxes?.cofinsStSoldQuantity,
            cofinsStRealAliquot: data?.itemTaxes?.cofinsStRealAliquot,
          },

          irpjCsll: {
            csllWithheldRate: data?.itemTaxes?.csllWithheldRate,
            irrfWithheldRate: data?.itemTaxes?.irrfWithheldRate,
            csllBaseCalculationValue: data?.itemTaxes?.csllBaseCalculationValue,
            csllWithheldValue: data?.itemTaxes?.csllWithheldValue,
            irrfBaseCalculationValue: data?.itemTaxes?.irrfBaseCalculationValue,
            irrfWithheldValue: data?.itemTaxes?.irrfWithheldValue,
          },

          is: {
            selectiveTaxCst: data?.itemTaxes?.selectiveTaxCst,
            selectiveTaxClassificationCode:
              data?.itemTaxes?.selectiveTaxClassificationCode,
            selectiveTaxBaseCalculationValue:
              data?.itemTaxes?.selectiveTaxBaseCalculationValue,
            selectiveTaxAliquot: data?.itemTaxes?.selectiveTaxAliquot,
            selectiveTaxSpecific: data?.itemTaxes?.selectiveTaxSpecific,
            selectiveTaxTaxableUnitMeasure:
              data?.itemTaxes?.selectiveTaxTaxableUnitMeasure,
            selectiveTaxTaxableQuantity:
              data?.itemTaxes?.selectiveTaxTaxableQuantity,
            selectiveTaxValue: data?.itemTaxes?.selectiveTaxValue,
          },

          ibsCbs: {
            ibsCbsCst: data.itemTaxes?.ibsCbsCst,
            ibsCbsTaxClassification: data.itemTaxes?.ibsCbsTaxClassification,
            ibsCbsDonation: data.itemTaxes?.ibsCbsDonation,
            ibsCbsBaseCalculation: data.itemTaxes?.ibsCbsBaseCalculation,
            ibsUfAliquot: data.itemTaxes?.ibsUfAliquot,
            ibsUfReductionAliquot: data.itemTaxes?.ibsUfReductionAliquot,
            ibsUfEffectiveAliquot: data.itemTaxes?.ibsUfEffectiveAliquot,
            ibsUfDefermentPercentage: data.itemTaxes?.ibsUfDefermentPercentage,
            ibsUfDefermentValue: data.itemTaxes?.ibsUfDefermentValue,
            ibsUfTaxReturned: data.itemTaxes?.ibsUfTaxReturned,
            ibsUfValue: data.itemTaxes?.ibsUfValue,
            ibsMunicipalAliquot: data.itemTaxes?.ibsMunicipalAliquot,
            ibsMunicipalReductionAliquot:
              data.itemTaxes?.ibsMunicipalReductionAliquot,
            ibsMunicipalEffectiveAliquot:
              data.itemTaxes?.ibsMunicipalEffectiveAliquot,
            ibsMunicipalDeferralPercentage:
              data.itemTaxes?.ibsMunicipalDeferralPercentage,
            ibsMunicipalDeferralValue:
              data.itemTaxes?.ibsMunicipalDeferralValue,
            ibsMunicipalTaxReturned: data.itemTaxes?.ibsMunicipalTaxReturned,
            ibsMunicipalValue: data.itemTaxes?.ibsMunicipalValue,
            ibsTotalValue: data.itemTaxes?.ibsTotalValue,
            cbsAliquot: data.itemTaxes?.cbsAliquot,
            cbsReductionAliquot: data.itemTaxes?.cbsReductionAliquot,
            cbsEffectiveAliquot: data.itemTaxes?.cbsEffectiveAliquot,
            cbsDeferralPercentage: data.itemTaxes?.cbsDeferralPercentage,
            cbsDeferralValue: data.itemTaxes?.cbsDeferralValue,
            cbsTaxReturned: data.itemTaxes?.cbsTaxReturned,
            cbsValue: data.itemTaxes?.cbsValue,
            // Regular taxation ---
            regularTaxIbsCbsCst: data.itemTaxes?.regularTaxIbsCbsCst,
            regularTaxIbsCbsClassificationCode:
              data.itemTaxes?.regularTaxIbsCbsClassificationCode,
            regularTaxIbsUfAliquotPercentage:
              data.itemTaxes?.regularTaxIbsUfAliquotPercentage,
            regularTaxIbsUfValue: data.itemTaxes?.regularTaxIbsUfValue,
            regularTaxIbsMunicipalAliquotPercentage:
              data.itemTaxes?.regularTaxIbsMunicipalAliquotPercentage,
            regularTaxIbsMunicipalValue:
              data.itemTaxes?.regularTaxIbsMunicipalValue,
            regularTaxCbsAliquotPercentage:
              data.itemTaxes?.regularTaxCbsAliquotPercentage,
            regularTaxCbsValue: data.itemTaxes?.regularTaxCbsValue,
            // GovernmentProcurement ---
            govPurchaseIbsUfAliquotPercentage:
              data.itemTaxes?.govPurchaseIbsUfAliquotPercentage,
            govPurchaseIbsUfValue: data.itemTaxes?.govPurchaseIbsUfValue,
            govPurchaseIbsMunicipalAliquotPercentage:
              data.itemTaxes?.govPurchaseIbsMunicipalAliquotPercentage,
            govPurchaseIbsMunicipalValue:
              data.itemTaxes?.govPurchaseIbsMunicipalValue,
            govPurchaseCbsAliquotPercentage:
              data.itemTaxes?.govPurchaseCbsAliquotPercentage,
            govPurchaseCbsValue: data.itemTaxes?.govPurchaseCbsValue,
            // Single Phase ---
            monoQuantity: data.itemTaxes?.monoQuantity,
            monoAdRemIbsAliquotPercentage:
              data.itemTaxes?.monoAdRemIbsAliquotPercentage,
            monoAdRemCbsAliquotPercentage:
              data.itemTaxes?.monoAdRemCbsAliquotPercentage,
            monoIbsValue: data.itemTaxes?.monoIbsValue,
            monoCbsValue: data.itemTaxes?.monoCbsValue,
            monoRetentionQuantity: data.itemTaxes?.monoRetentionQuantity,
            monoRetentionAdRemIbsAliquotPercentage:
              data.itemTaxes?.monoRetentionAdRemIbsAliquotPercentage,
            monoRetentionIbsValue: data.itemTaxes?.monoRetentionIbsValue,
            monoRetentionAdRemCbsAliquotPercentage:
              data.itemTaxes?.monoRetentionAdRemCbsAliquotPercentage,
            monoRetentionCbsValue: data.itemTaxes?.monoRetentionCbsValue,
            monoRetainedQuantity: data.itemTaxes?.monoRetainedQuantity,
            monoRetainedAdRemIbsAliquotPercentage:
              data.itemTaxes?.monoRetainedAdRemIbsAliquotPercentage,
            monoRetainedIbsValue: data.itemTaxes?.monoRetainedIbsValue,
            monoRetainedAdRemCbsAliquotPercentage:
              data.itemTaxes?.monoRetainedAdRemCbsAliquotPercentage,
            monoRetainedCbsValue: data.itemTaxes?.monoRetainedCbsValue,
            monoDeferralIbsPercentage:
              data.itemTaxes?.monoDeferralIbsPercentage,
            monoDeferralIbsValue: data.itemTaxes?.monoDeferralIbsValue,
            monoDeferralCbsPercentage:
              data.itemTaxes?.monoDeferralCbsPercentage,
            monoDeferralCbsValue: data.itemTaxes?.monoDeferralCbsValue,
            monoItemIbsTotal: data.itemTaxes?.monoItemIbsTotal,
            monoItemCbsTotal: data.itemTaxes?.monoItemCbsTotal,
            // Competency Adjustment ---
            competenceApuration: data.itemTaxes?.competenceApuration
              ? moment(data.itemTaxes?.competenceApuration)
              : undefined,
            competenceIbsValue: data.itemTaxes?.competenceIbsValue,
            competenceCbsValue: data.itemTaxes?.competenceCbsValue,
            // Credit Transfers/Reversal ---
            creditTransferIbsValue: data.itemTaxes?.creditTransferIbsValue,
            creditTransferCbsValue: data.itemTaxes?.creditTransferCbsValue,
            creditReversalIbsValue: data.itemTaxes?.creditReversalIbsValue,
            creditReversalCbsValue: data.itemTaxes?.creditReversalCbsValue,
            // Presumed Credit ---
            classificationCodePresumedCredit:
              data.itemTaxes?.classificationCodePresumedCredit,
            ibsPresumedCreditPercentage:
              data.itemTaxes?.ibsPresumedCreditPercentage,
            ibsPresumedCreditValue: data.itemTaxes?.ibsPresumedCreditValue,
            ibsPresumedCreditSuspensiveConditionValue:
              data.itemTaxes?.ibsPresumedCreditSuspensiveConditionValue,
            cbsPresumedCreditPercentage:
              data.itemTaxes?.cbsPresumedCreditPercentage,
            cbsPresumedCreditValue: data.itemTaxes?.cbsPresumedCreditValue,
            cbsPresumedCreditSuspensiveConditionValue:
              data.itemTaxes?.cbsPresumedCreditSuspensiveConditionValue,
            // Presumed Credit IBS ZFM ---
            ibsZfmAssessmentPeriod: data.itemTaxes?.ibsZfmAssessmentPeriod
              ? moment(data.itemTaxes?.ibsZfmAssessmentPeriod, 'YYYY-MM')
              : undefined,
            ibsZfmPresumedCreditType: data.itemTaxes?.ibsZfmPresumedCreditType,
            ibsZfmPresumedCreditValue:
              data.itemTaxes?.ibsZfmPresumedCreditValue,
          },
        },
        fuel: data?.fuel,
      };
    },
    [],
  );

  return [fetchFields, invoiceNoteItemDetail.isLoading];
}

type NCMChangeFillFieldsReturn = [
  (
    invoiceItemId?: number,
    ncmId?: number,
    invoiceType?: InvoiceType,
  ) => Promise<InvoicePartNcmChangeFormValues>,
  boolean,
];

export function useInvoicePartNcmChangeFillFieds(
  props?: Props,
): NCMChangeFillFieldsReturn {
  const ncmDetail = useNcmMutation();
  const invoiceNoteItemDetail = useInvoiceNoteItemDetail();
  const { data: invoice } = useInvoiceSettingsByCompany(props?.companyId);

  const fetchFields = useCallback(
    async (
      invoiceItemId?: number,
      ncmId?: number,
      invoiceType?: InvoiceType,
    ) => {
      if (invoiceItemId) {
        await invoiceNoteItemDetail.mutateAsync(invoiceItemId);
      }

      const data = invoiceNoteItemDetail.data;

      let ncm = {} as Ncm | undefined;
      let invoiceSettings;

      if (ncmId || data?.ncmId) {
        ncm = ncmId
          ? await ncmDetail.mutateAsync(ncmId)
          : data?.ncmId
          ? await ncmDetail.mutateAsync(data?.ncmId)
          : undefined;

        if (invoiceType === InvoiceType.NFC) {
          invoiceSettings = invoice?.configNfc;
        } else if (invoiceType === InvoiceType.NFE) {
          invoiceSettings = invoice?.configNfe;
        }
      }

      const getIcmsType = (taxRegime?: TaxRegimeEnum, type?: string) => {
        if (
          taxRegime === TaxRegimeEnum.simple ||
          taxRegime === TaxRegimeEnum.simplesNationalMei
        ) {
          return optionsCsosnCodes
            .filter((option) =>
              taxRegime === TaxRegimeEnum.simplesNationalMei
                ? taxesMeiOptions.includes(option.code)
                : true,
            )
            .find((a) => a.code === type)?.id;
        } else {
          return optionsCstCodes.find((a) => a.code === type)?.id;
        }
      };

      const icmsType = getIcmsType(
        invoiceSettings?.taxRegimeDefault,
        invoiceSettings?.taxRegimeDefault === TaxRegimeEnum.simple ||
          invoiceSettings?.taxRegimeDefault === TaxRegimeEnum.simplesNationalMei
          ? ncm?.csosnIcms
          : ncm?.cstIcms,
      );

      const pisCst = (ncm?.cstPis ??
        invoiceSettings?.pisCstDefault ??
        data?.itemTaxes?.pisCst) as PisCofinsCst;

      const pisAliquot =
        ncm?.aliquotPis ??
        invoiceSettings?.pisAliquotDefault ??
        data?.itemTaxes?.pisAliquot;

      const cofinsCst = (ncm?.cstCofins ??
        invoiceSettings?.cofinsCstDefault ??
        data?.itemTaxes?.cofinsCst) as PisCofinsCst;

      const cofinsAliquot =
        ncm?.aliquotCofins ??
        invoiceSettings?.cofinsAliquotDefault ??
        data?.itemTaxes?.cofinsAliquot;

      return {
        general: {
          cest:
            ncm?.cest ?? data?.cest
              ? applyMask(ncm?.cest ?? data?.cest, cestMask)
              : undefined,
        },
        itemTaxes: {
          icms: {
            icmsStValueAddedMarginPercentage:
              ncm?.mva ?? data?.itemTaxes?.icmsStValueAddedMarginPercentage,
            taxRegime:
              invoiceSettings?.taxRegimeDefault || data?.itemTaxes?.taxRegime,
            icmsBaseCalculationDeterminationMode:
              calculationDeterminationMode[
                ncm?.modalityDeterminationBcIcms as number
              ] ||
              invoiceSettings?.icmsBaseCalculationDeterminationModeDefault ||
              data?.itemTaxes?.icmsBaseCalculationDeterminationMode,
            icmsCst:
              icmsType ??
              invoiceSettings?.icmsCstDefault ??
              data?.itemTaxes?.icmsCst,
            icmsAliquot:
              ncm?.aliquotIcms ??
              invoiceSettings?.icmsAliquotDefault ??
              data?.itemTaxes?.icmsAliquot,
          },

          ipi: {
            ipiCst: (ncm?.cstIpi ??
              invoiceSettings?.ipiCstDefault ??
              data?.itemTaxes?.ipiCst) as IpiCst,
            ipiAliquot:
              ncm?.aliquotIpi ??
              invoiceSettings?.ipiAliquotDefault ??
              data?.itemTaxes?.ipiAliquot,
          },

          pis: {
            pisCst,
            pisAliquot:
              pisCst === PisCofinsCst.TAXABLE_OPERATION_MONOPHASIC
                ? undefined
                : pisAliquot,
          },

          cofins: {
            cofinsCst,
            cofinsAliquot:
              cofinsCst === PisCofinsCst.TAXABLE_OPERATION_MONOPHASIC
                ? undefined
                : cofinsAliquot,
          },

          is: {
            selectiveTaxAliquot:
              ncm?.selectiveTaxAliquot ??
              invoiceSettings?.selectiveTaxAliquotDefault ??
              data?.itemTaxes?.selectiveTaxAliquot,
            selectiveTaxCst:
              ncm?.selectiveTaxCst ||
              invoiceSettings?.selectiveTaxCstDefault ||
              data?.itemTaxes?.selectiveTaxCst,
            selectiveTaxClassificationCode:
              ncm?.selectiveTaxClassificationCode ||
              invoiceSettings?.selectiveTaxClassificationCodeDefault ||
              data?.itemTaxes?.selectiveTaxClassificationCode,
          },

          ibsCbs: {
            ibsCbsCst:
              ncm?.ibsCbsCst ||
              invoiceSettings?.ibsCbsCstDefault ||
              data?.itemTaxes?.ibsCbsCst,
            ibsCbsTaxClassification:
              ncm?.ibsCbsTaxClassification ||
              invoiceSettings?.ibsCbsTaxClassificationDefault ||
              data?.itemTaxes?.ibsCbsTaxClassification,
            ibsUfAliquot:
              ncm?.ibsUfAliquotDefault ??
              invoiceSettings?.ibsUfAliquotDefault ??
              data?.itemTaxes?.ibsUfAliquot,
            ibsMunicipalAliquot:
              ncm?.ibsMunicipalAliquotDefault ??
              invoiceSettings?.ibsMunicipalAliquotDefault ??
              data?.itemTaxes?.ibsMunicipalAliquot,
            cbsAliquot:
              ncm?.cbsAliquot ??
              invoiceSettings?.cbsAliquot ??
              data?.itemTaxes?.cbsAliquot,
          },
        },
      } as InvoicePartFormValues;
    },
    [],
  );

  return [fetchFields, invoiceNoteItemDetail.isLoading];
}

interface CstCsosnChangeFillFieldsParams {
  companyId?: number;
}

type CSTCSOSNChangeFillFieldsReturn = [
  (params: {
    invoiceItemId?: number;
    ncmId?: number;
    invoiceType?: InvoiceType;
  }) => Promise<Partial<InvoicePartNcmChangeFormValues>>,
  boolean,
];

export function useInvoicePartCstCsosnChangeFillFieds(
  props?: CstCsosnChangeFillFieldsParams,
): CSTCSOSNChangeFillFieldsReturn {
  const ncmDetail = useNcmMutation();
  const invoiceNoteItemDetail = useInvoiceNoteItemDetail();
  const { data: invoice } = useInvoiceSettingsByCompany(props?.companyId);

  const fetchFields = useCallback(
    async ({
      invoiceItemId,
      ncmId,
      invoiceType,
    }: {
      invoiceItemId?: number;
      ncmId?: number;
      invoiceType?: InvoiceType;
    }) => {
      if (invoiceItemId) {
        await invoiceNoteItemDetail.mutateAsync(invoiceItemId);
      }

      const data = invoiceNoteItemDetail.data;

      let ncm = {} as Ncm | undefined;
      let invoiceSettings;

      if (ncmId || data?.ncmId) {
        ncm = ncmId
          ? await ncmDetail.mutateAsync(ncmId)
          : data?.ncmId
          ? await ncmDetail.mutateAsync(data?.ncmId)
          : undefined;

        if (invoiceType === InvoiceType.NFC) {
          invoiceSettings = invoice?.configNfc;
        } else if (invoiceType === InvoiceType.NFE) {
          invoiceSettings = invoice?.configNfe;
        }
      }

      return {
        itemTaxes: {
          icms: {
            icmsBaseCalculationDeterminationMode:
              calculationDeterminationMode[
                ncm?.modalityDeterminationBcIcms as number
              ] ??
              invoiceSettings?.icmsBaseCalculationDeterminationModeDefault ??
              data?.itemTaxes?.icmsBaseCalculationDeterminationMode,
            icmsAliquot:
              ncm?.aliquotIcms ??
              invoiceSettings?.icmsAliquotDefault ??
              data?.itemTaxes?.icmsAliquot,
          },
        },
      } as InvoicePartNcmChangeFormValues;
    },
    [],
  );

  return [
    fetchFields,
    invoiceNoteItemDetail.isLoading || invoiceNoteItemDetail.isLoading,
  ];
}
